import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { coverEditorMessages } from '../coverEditorMessages';
import { CoverThumb } from './CoverThumb';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StagePillsProps {
  doc: CoverDoc;
  isSnapOn: boolean;
  isSafeOn: boolean;
  isSmallOn: boolean;
  onToggleSnap: () => void;
  onToggleSafe: () => void;
  onToggleSmall: () => void;
}

/** The side of the small preview: the size of a cover in a track list of a streaming app. */
export const SMALL_PREVIEW_PX = 64;

/**
 * The three switches under the stage (design cover-editor b1): the snap guides, the safe margin,
 * and the preview at 64 px, which shows if the title still reads in a track list.
 */
const StagePillsComponent: React.FC<StagePillsProps> = (props) => {
  const { doc, isSnapOn, isSafeOn, isSmallOn, onToggleSnap, onToggleSafe, onToggleSmall } = props;
  const intl = useIntl();
  return (
    <div className={styles.stageBar} onPointerDown={(event) => event.stopPropagation()} role="presentation">
      <button
        type="button"
        className={clsx(styles.pill, isSnapOn && styles.pillOn)}
        aria-pressed={isSnapOn}
        onClick={onToggleSnap}
      >
        {intl.formatMessage(isSnapOn ? coverEditorMessages.guidesOn : coverEditorMessages.guidesOff)}
      </button>
      <button
        type="button"
        className={clsx(styles.pill, isSafeOn && styles.pillOn)}
        aria-pressed={isSafeOn}
        onClick={onToggleSafe}
      >
        {intl.formatMessage(coverEditorMessages.safeMargin)}
      </button>
      <button
        type="button"
        className={clsx(styles.pill, isSmallOn && styles.pillOn)}
        aria-pressed={isSmallOn}
        onClick={onToggleSmall}
      >
        {intl.formatMessage(coverEditorMessages.previewSmall, { size: SMALL_PREVIEW_PX })}
      </button>
      {isSmallOn && (
        <span className={styles.smallPreview}>
          <CoverThumb
            doc={doc}
            size={SMALL_PREVIEW_PX}
            label={intl.formatMessage(coverEditorMessages.previewSmallLabel)}
          />
        </span>
      )}
    </div>
  );
};

export const StagePills = TypedMemo(StagePillsComponent);
